import { isRawRank, RAW_RANKS, type RawRank } from "@tcg/ui";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export { isRawRank, RAW_RANKS, type RawRank };

/**
 * Admin rank colours: darker than the storefront badge so white letters stay ≥ 4.5:1 on every
 * rank (A #047857 5.5:1 · B #1d4ed8 6.7:1 · C #b45309 5.0:1 · D #b91c1c 6.5:1) — the letter is
 * always printed too, colour is never the only cue.
 */
export const RANK_STYLE: Readonly<Record<RawRank, string>> = {
  A: "bg-[#047857] text-white",
  B: "bg-[#1d4ed8] text-white",
  C: "bg-[#b45309] text-white",
  D: "bg-[#b91c1c] text-white",
};

export interface RankBadgeProps {
  rank: RawRank;
  /** `raw_rank_damaged_flag` (only meaningful for D). */
  damaged?: boolean;
  /** Show the description next to the letter (tables use the letter only). */
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

/** Shop condition scale for raw cards (CLAUDE.md): A 美品 · B 軽い傷 · C 傷あり · D ダメージ. */
export function RankBadge({ rank, damaged, showLabel, size = "md", className }: RankBadgeProps) {
  const { t } = useTranslation();
  const label = t(`rank.${rank}`);
  const full = `${t("rank.badge_label", { rank, label })}${damaged && rank === "D" ? ` (${t("rank.damaged")})` : ""}`;
  const sizeClass =
    size === "sm"
      ? "h-5 min-w-5 text-xs"
      : size === "lg"
        ? "h-9 min-w-9 text-lg"
        : "h-6 min-w-6 text-sm";
  return (
    <span
      data-slot="rank-badge"
      data-rank={rank}
      data-damaged={damaged && rank === "D" ? "true" : undefined}
      role="img"
      aria-label={full}
      title={`${full}\n${t("rank.scale")}`}
      className={cn("inline-flex items-center gap-1 font-bold", className)}
    >
      <span
        aria-hidden
        className={cn(
          "inline-flex items-center justify-center rounded-sm px-1 leading-none",
          sizeClass,
          RANK_STYLE[rank],
        )}
      >
        {rank}
        {damaged && rank === "D" ? "!" : null}
      </span>
      {showLabel ? (
        <span aria-hidden className="text-xs font-medium">
          {label}
          {damaged && rank === "D" ? ` · ${t("rank.damaged")}` : null}
        </span>
      ) : null}
    </span>
  );
}
